import React, { useEffect, useState } from 'react'
import style from './CalendarExternal.module.css'
import dayjs from 'dayjs'
import { dateToDateString, getMonday } from '../../common/utils/date'
import {
  ExternAppointment,
  ExternAppointmentQuery,
} from 'modules/calendar/models/ExternAppointment'
import { Query, QueryParam } from '../../common/api/Query'
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Modal,
  TextField,
  Typography,
  MenuItem,
} from '@mui/material'
import { getUserContainer } from '../../container/user-module'
import { capitalize, reduceString } from '../../common/utils/strings'
import { CalendarModal } from './CalendarModal'
import { LOGGED_USER_SERVICE_KEY } from '../../modules/users/container'
import { getCalendarContainer } from '../../container/calendar-module'
import { EXTERN_APPOINTMENT_SERVICE_KEY } from '../../modules/calendar'
import { LoggedUserService } from '../../modules/users/services/LoggedUserService'
import { useTranslation } from 'react-i18next'
import { ROUTE_APPOINTMENT_DETAIL } from '../../routes/routes-constants'
import { useIsOpenMenuLeftContext } from '../../common/utils/isOpenMenuLeftContext'
import citaIcon from '../../assets/calendar/cita.svg'
import beforeIcon from '../../assets/calendar/before.svg'
import afterIcon from '../../assets/calendar/after.svg'
import { useNavigate } from 'react-router-dom'
import { useCircleContext } from '../../common/context/CircleContext'
import { IExternAppointmentService } from 'modules/calendar/services/ExternAppointmentService'
import {
  ExternAppointmentStatus,
  externAppointmentStatusTypes,
} from 'modules/calendar/enums/ExternAppointmentStatus'

const appointmentService = getCalendarContainer().get<IExternAppointmentService>(
  EXTERN_APPOINTMENT_SERVICE_KEY
)

const hours = (): string[] => {
  const hourRange = []
  for (let i = 7; i < 23; i++) {
    if (i < 10) {
      hourRange.push('0' + i + ':00')
    } else {
      hourRange.push(i + ':00')
    }
  }
  return hourRange
}

type CalendarExternalProps = {
  selectedDate: Date
  externalProfessionalID?: string
}

type WeekEvent = {
  event: ExternAppointment
}
const loggedUserService = getUserContainer().get<LoggedUserService>(LOGGED_USER_SERVICE_KEY)

export function CalendarExternal(props: CalendarExternalProps) {
  const { t } = useTranslation()
  const loggedUser = loggedUserService.get()
  const [events, setEvents] = useState<ExternAppointment[]>([])
  const [monday, setMonday] = useState<Date>(getMonday(props.selectedDate))
  const [openModalMultiEvent, setOpenModalMultiEvent] = useState<boolean>(false)
  const [weekOffset, setWeekOffset] = useState<number>(0)
  const { openMenuLeft } = useIsOpenMenuLeftContext()
  const navigate = useNavigate()
  const { circle } = useCircleContext()
  const [dateTimeMultiEvents, setDateTimeMultiEvents] = useState<{
    date: Date | string
    startHour: string
    endHour: string
  }>()
  const [statusFilter, setStatusFilter] = useState<ExternAppointmentStatus | string>('every')
  monday.setHours(0, 0, 0, 0)
  useEffect(() => {
    const startDate = dayjs(monday)
      .add(7 * weekOffset, 'day')
      .toDate()
    const finishDate = new Date(startDate)
    finishDate.setDate(startDate.getDate() + 7)
    const queryStatusFilter = [
      new QueryParam<ExternAppointmentQuery>('status', [Number(statusFilter)]),
      new QueryParam<ExternAppointmentQuery>('professionalId', loggedUser?.id ?? ''),
      new QueryParam<ExternAppointmentQuery>('dateFrom', startDate.toISOString()),
      new QueryParam<ExternAppointmentQuery>('dateTo', finishDate.toISOString()),
    ]

    appointmentService
      .getFilteredList(
        new Query({
          query: isNaN(Number(statusFilter))
            ? [
                new QueryParam<ExternAppointmentQuery>('professionalId', loggedUser?.id ?? ''),
                new QueryParam<ExternAppointmentQuery>('dateFrom', startDate.toISOString()),
                new QueryParam<ExternAppointmentQuery>('dateTo', finishDate.toISOString()),
              ]
            : queryStatusFilter,
        })
      )
      .subscribe((res) => {
        setEvents(res.items)
      })
  }, [monday, circle, statusFilter, weekOffset])

  const getStatusColor = (status: number) => {
    switch (status) {
      case ExternAppointmentStatus.Pending:
        return '#F0C982'
      case ExternAppointmentStatus.Accepted:
        return '#C1E4C2'
      case ExternAppointmentStatus.Completed:
        return '#96C5EA'
      case ExternAppointmentStatus.Finalized:
        return '#B47FBD'
      case ExternAppointmentStatus.Canceled:
        return '#F19189'
      default:
        return '#cfd8dc'
    }
  }

  const handleFilter = (event: React.ChangeEvent<HTMLInputElement>) => {
    setStatusFilter(event.target.value)
  }

  function WeekEvent(props: WeekEvent): JSX.Element {
    let duration
    if (
      dayjs(props.event.dateFrom).format('DD').toString() ===
      dayjs(props.event.dateTo).format('DD').toString()
    ) {
      duration =
        Number(dayjs(props.event.dateTo).format('HH')) -
        Number(dayjs(props.event.dateFrom).format('HH'))
    }

    return (
      <>
        {props.event.dateFrom === props.event.dateTo ? (
          <Tooltip
            title={`${externAppointmentStatusTypes()[props.event.status]}: ${reduceString(
              props.event.patient.firstName,
              15
            )} (${dayjs(props.event.dateFrom).format('HH:mm')})`}
          >
            <Box
              className={style.eventNotSameDay}
              onClick={() => handlerClick(props.event)}
              style={{
                backgroundColor: getStatusColor(props.event.status),
              }}
            >
              <div className={style.iconEventCategoryNotSameDay}>{<img src={citaIcon} />}</div>
              <h3 className={style.eventTitleNotSameDay}>{`${dayjs(props.event.dateFrom).format(
                'HH:mm'
              )} - ${dayjs(props.event.dateTo).format('HH:mm')}`}</h3>
            </Box>
          </Tooltip>
        ) : (
          <Tooltip
            title={`${externAppointmentStatusTypes()[props.event.status]}: ${reduceString(
              props.event.patient.firstName,
              15
            )} (${dayjs(props.event.dateFrom).format('HH:mm')})`}
          >
            <Box
              className={style.event}
              onClick={() => handlerClick(props.event)}
              style={{
                ['--duration-height' as any]: (duration || -1) + 1,
                ['--col' as any]: getStatusColor(props.event.status),
                backgroundColor: getStatusColor(props.event.status),
                paddingBottom: '8px',
              }}
            >
              {<img src={citaIcon} />}
              <h3 className={style.eventTitle}>{`${dayjs(props.event.dateFrom).format(
                'HH:mm'
              )} - ${dayjs(props.event.dateTo).format('HH:mm')}`}</h3>
            </Box>
          </Tooltip>
        )}
      </>
    )
  }

  const weekDays = (): Date[] => {
    const startDate = dayjs(monday)
      .add(7 * weekOffset, 'day')
      .toDate()
    let datesRange = [startDate]

    for (let i = 1; i < 7; i++) {
      let newDate = new Date(startDate)
      newDate.setDate(startDate.getDate() + i)
      datesRange.push(newDate)
    }
    return datesRange
  }

  const handlerClick = (e: ExternAppointment) =>
    navigate(ROUTE_APPOINTMENT_DETAIL.replace(':id', e.id ?? ''))

  const handleOpenModalMultiEvent = (date: string, startHour: string, endHour: string) => {
    setDateTimeMultiEvents({ date, startHour, endHour })
    setOpenModalMultiEvent(true)
  }

  const handleCloseModalMultiEvent = () => setOpenModalMultiEvent(false)

  return (
    <div
      style={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        marginLeft: openMenuLeft ? '50px' : '',
      }}
    >
      <div
        style={{
          maxWidth: openMenuLeft ? 'calc(100% - 250px)' : 'calc(100% - 300px)',
          paddingTop: 120,
        }}
      >
        <div className={style.container}>
          <div className={style.filterRow}>
            <Typography>{t('status') + ':'}</Typography>
            <TextField
              select
              variant={'outlined'}
              className={style.statusFilter}
              value={statusFilter}
              label={''}
              onChange={handleFilter}
              required
            >
              <MenuItem className={style.selectItem} value={'every'}>
                {t('every')}
              </MenuItem>
              {Object.keys(ExternAppointmentStatus)
                .filter((s) => isNaN(Number(s)))
                .map((status: string) => (
                  <MenuItem
                    className={style.selectItem}
                    value={ExternAppointmentStatus[status as keyof typeof ExternAppointmentStatus]}
                    key={status}
                  >
                    {t(status.toLowerCase())}
                  </MenuItem>
                ))}
            </TextField>
            <Box className={style.weekChangerContainer}>
              <Box className={style.weekChanger} onClick={() => setWeekOffset(weekOffset - 1)}>
                <img src={beforeIcon}></img>
              </Box>
              <Typography className={style.week}>
                {dayjs(monday)
                  .add(7 * weekOffset, 'day')
                  .format('D')}{' '}
                -{' '}
                {dayjs(monday)
                  .add(6 + 7 * weekOffset, 'day')
                  .format('D MMMM')}
              </Typography>
              <Box className={style.weekChanger} onClick={() => setWeekOffset(weekOffset + 1)}>
                <img src={afterIcon}></img>
              </Box>
            </Box>
          </div>
          <Table
            sx={{
              borderRadius: '27px',
              border: 'none',
              background: 'transparent',
              width: 'auto',
              '& td': {
                borderStyle: 'dotted',
                borderColor: '#2e7161',
                '&:nth-child(odd)': {
                  backgroundColor: '',
                },
                '&:last-child': {
                  borderRightWidth: 1,
                  borderRightStyle: 'dotted',
                  borderColor: '#2e7161',
                },
                '&:first-child': {
                  borderLeftWidth: 1,
                  borderLeftStyle: 'dotted',
                  borderColor: '#2e7161',
                },
              },
              '& tr': {
                borderStyle: 'dotted',
                borderColor: '#2e7161',
                '&:nth-child(odd)': {
                  backgroundColor: '',
                },
              },
              '& th': {
                borderBottomStyle: 'dotted',
                borderColor: '#2e7161',
                padding: '14px 0 5px 0',
              },
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                width: openMenuLeft ? '' : '100%',
                marginLeft: openMenuLeft ? 30 : '',
                marginRight: openMenuLeft ? 30 : '',
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell className={style.dayEmpty}></TableCell>
                  {weekDays().map((d, i) => (
                    <TableCell
                      className={style.weekHeader}
                      key={dayjs(d).locale(navigator.language).format('ddd')}
                    >
                      <div className={`${i === 0 ? style.day1 : ''}`} key={dateToDateString(d)}>
                        <div
                          className={
                            new Date(d).getDay() === new Date().getDay() && weekOffset === 0
                              ? style.weekNoDay
                              : style.weekDay
                          }
                        >
                          <div className={style.weekDayNumber}>{dayjs(d).format('DD')}</div>
                          <div className={style.weekDayName}>
                            {window.innerWidth > 480
                              ? capitalize(dayjs(d).locale(navigator.language).format('ddd'))
                              : capitalize(dayjs(d).locale(navigator.language).format('dd'))}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {hours().map((h, i) => (
                  <TableRow key={i}>
                    <TableCell className={style.hourRow}>
                      <div className={style.hour}>{h}</div>
                    </TableCell>
                    {weekDays().map((d) => (
                      <TableCell className={style.eventRow} key={String(d) + String(h)}>
                        <div className={style.containerEvent}>
                          {(() => {
                            const hourInt = parseInt(h.split(':')[0])

                            const eventsSameDay = events
                              .filter((e) => dayjs(e.dateFrom).isSame(d, 'day'))
                              .sort((a, b) => dayjs(a.dateFrom).diff(dayjs(b.dateFrom)))

                            const chains: ExternAppointment[][] = []
                            let currentChain: ExternAppointment[] = []

                            for (let i = 0; i < eventsSameDay.length; i++) {
                              const current = eventsSameDay[i]

                              if (currentChain.length === 0) {
                                currentChain.push(current)
                                continue
                              }

                              const prev = currentChain[currentChain.length - 1]
                              const prevHour = dayjs(prev.dateFrom).hour()
                              const currentHour = dayjs(current.dateFrom).hour()

                              if (
                                prevHour === currentHour ||
                                dayjs(prev.dateTo).isSame(dayjs(current.dateFrom))
                              ) {
                                currentChain.push(current)
                              } else {
                                chains.push(currentChain)
                                currentChain = [current]
                              }
                            }

                            if (currentChain.length > 0) {
                              chains.push(currentChain)
                            }

                            const eventsInSameHour =
                              chains.find((chain) => {
                                const startHour = dayjs(chain[0].dateFrom).hour()
                                return hourInt === startHour
                              }) || []

                            const chainMaxDuration = Math.max(
                              ...eventsInSameHour.map((event) => {
                                return dayjs(event.date).get('hours') + 1 - hourInt
                              }),
                              1
                            )

                            const statusCounts = eventsInSameHour.reduce(
                              (acc: Record<number, number>, event) => {
                                acc[event.status] = (acc[event.status] || 0) + 1
                                return acc
                              },
                              {}
                            )

                            const totalEvents = eventsInSameHour.length

                            if (totalEvents > 1) {
                              return (
                                <div
                                  className={style.multiEventContainer}
                                  style={{
                                    height: `${chainMaxDuration * 35}px`,
                                  }}
                                  onClick={() => {
                                    const startHour = `${hourInt}:00`
                                    const endHour = `${hourInt + chainMaxDuration}:00`
                                    handleOpenModalMultiEvent(
                                      dayjs(d).format('DD').toString(),
                                      startHour,
                                      endHour
                                    )
                                  }}
                                >
                                  <div className={style.multiEventIcon}>
                                    <img src={citaIcon} />
                                  </div>
                                  <div className={style.statusBadgesContainer}>
                                    {Object.entries(statusCounts).map(([status, count]) => (
                                      <div
                                        key={status}
                                        className={style.statusBadge}
                                        style={{ backgroundColor: getStatusColor(Number(status)) }}
                                      >
                                        {
                                          externAppointmentStatusTypes()[
                                            Number(status) as ExternAppointmentStatus
                                          ]
                                        }
                                        : {count}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )
                            }

                            return eventsInSameHour.map((event) =>
                              dayjs(event.dateFrom).format('HH').toString() !==
                              dayjs(event.dateTo).subtract(1, 'minute').format('HH').toString() ? (
                                <WeekEvent event={event} key={event.id} />
                              ) : (
                                <Box
                                  className={style.eventNotSameDay}
                                  onClick={() => handlerClick(event)}
                                  style={{
                                    backgroundColor: getStatusColor(event.status),
                                  }}
                                  key={event.id}
                                >
                                  <div className={style.iconEventCategoryNotSameDay}>
                                    {<img src={citaIcon} />}
                                  </div>
                                  <h3 className={style.eventTitleNotSameDay}>
                                    {dayjs(event.dateFrom).format('HH:mm')}-
                                    {dayjs(event.dateTo).format('HH:mm')}
                                  </h3>
                                </Box>
                              )
                            )
                          })()}
                        </div>
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </div>
            <div className={style.buttonAddAppointmentContainer}></div>
          </Table>
          {dateTimeMultiEvents && (
            <Modal
              open={openModalMultiEvent}
              className={style.eventModal}
              onClose={handleCloseModalMultiEvent}
            >
              <CalendarModal
                events={events}
                handleClose={handleCloseModalMultiEvent}
                handlerEventClick={handlerClick}
                date={dateTimeMultiEvents.date}
                startHour={dateTimeMultiEvents.startHour}
                endHour={dateTimeMultiEvents.endHour}
              />
            </Modal>
          )}
        </div>
      </div>
    </div>
  )
}
